import { useMemo } from "react";
import {
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Box, Button, Flex, Heading, Text, useToken } from "@chakra-ui/react";
import { compact } from "lodash";

import { DocumentType, getFragmentData } from "__generated__/gql";
import { SkuComponentType } from "__generated__/gql/graphql";
import ChakraReactTable from "components/Shared/ChakraReactTable";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import useStickyHeader from "components/Shared/ChakraReactTable/useStickyHeader";
import useSelectableRow from "components/Shared/ChakraReactTable/useSelectableRow";
import {
  makeNotReadonly,
  useChakraReactTableProps,
  usePrepApolloData,
} from "components/Shared/ChakraReactTable/utils";
import { formatUpc } from "components/Shared/Forms/UpcInput";
import { makeFormatCurrency, makeFormatCurrencyRange } from "utils/formatting";
import {
  formatIntegerCell,
  makeCellFormatter,
} from "components/Shared/ChakraReactTable/formatters";
import type { CreateNew } from "pages/products";
import AddWbr from "components/Shared/AddWbr";
import LabeledValueBadge from "components/Shared/LabeledValueBadge";
import ArchivedBadge from "components/Shared/ArchivedBadge";
import {
  tableSkuFragment,
  tableSkusForProductLineFragment,
  productLinesSkusTableFieldsFragment,
  ProductsTableData,
  productLinesSkusTableQuery,
} from "./queries";
import { SKU_COMPONENT_TYPE_LABELS_SHORT } from "./SkuPanel/SkuComponents/utils";
import {
  formatUnitNetWeight,
  sortSkuComponents,
  formatWeight,
  formatCaseCube,
} from "./utils";

const HEADER_HEIGHT = "3rem";

type ProductLineDatum = NonNullable<
  DocumentType<typeof productLinesSkusTableQuery>["productLinesList"]
>[number] &
  DocumentType<typeof productLinesSkusTableFieldsFragment>;

type SkuDatum = DocumentType<typeof tableSkuFragment>;

type Datum = SkuDatum | ProductLineDatum;

export type RowTypes = Datum["__typename"];

function getIsProductLine(datum: Datum): datum is ProductLineDatum {
  return datum.__typename === "ProductLine";
}

const col = createColumnHelper<Datum>();

const formatCurrency = makeFormatCurrency({ minimumFractionDigits: 2 });
const formatCurrencyCell = makeCellFormatter(formatCurrency);

const formatCurrencyRange = makeFormatCurrencyRange(formatCurrency);
const formatCurrencyRangeCell = makeCellFormatter(formatCurrencyRange);

const columns = [
  col.accessor("name", {
    header: "Name",
    cell: ({ row, renderValue }) => {
      const name = renderValue();
      if (getIsProductLine(row.original)) {
        return (
          <Flex
            alignItems="baseline"
            style={{ width: "calc(100vw - 20rem)" }}
            backgroundColor="inherit"
            pt={8}
            pb={4}
            gap={4}
          >
            <Heading
              size="sm"
              color="magenta.veryDark"
              sx={{ "[aria-selected=true] &": { color: "inherit" } }}
            >
              {name}
            </Heading>
            {row.original.isArchived && <ArchivedBadge mb="-.5em" />}
          </Flex>
        );
      }
      return (
        <>
          {name}
          {row.original.isArchived && <ArchivedBadge ml={4} mt="-.25em" />}
          <br />
          {row.original.internalSkuId && (
            <Box
              as="small"
              color="gray.400"
              fontFamily="mono"
              fontWeight="normal"
            >
              {row.original.internalSkuId}
            </Box>
          )}
        </>
      );
    },
    aggregatedCell: undefined,
    size: 225,
  }),
  col.display({
    id: "upcs",
    header: "UPCs",
    cell: ({ row }) => {
      if (getIsProductLine(row?.original) || !row.original.skuComponentsList) {
        return null;
      }
      return (
        <Flex gap={1} flexDirection="column" alignItems="flex-start">
          {sortSkuComponents(row.original.skuComponentsList).map(
            (skuComponent) => {
              if (!skuComponent.upc) return null;
              return (
                <LabeledValueBadge
                  key={skuComponent.id}
                  label={
                    SKU_COMPONENT_TYPE_LABELS_SHORT[
                      skuComponent.skuComponentType
                    ]
                  }
                  value={formatUpc(skuComponent.upc)}
                  labelProps={{ minW: "4.1em", textAlign: "center" }}
                  valueProps={{
                    fontFamily: "mono",
                    fontWeight: "normal",
                    fontSize: "xs",
                  }}
                />
              );
            },
          )}
        </Flex>
      );
    },
    size: 215,
  }),
  col.accessor("unitNetWeight", {
    header: "Unit Size",
    cell: (info) => {
      const sku = info.row.original;
      if (getIsProductLine(sku)) return null;
      const formatted = formatUnitNetWeight(
        sku.unitNetWeight,
        sku.unitNetWeightUnit,
      );
      if (!formatted) return null;
      return (
        <Box isTruncated>
          <AddWbr parts={compact(formatted.split(/([\d,.]+\s*)/))} />
        </Box>
      );
    },
    size: 120,
  }),
  col.accessor("unitCogs", {
    header: "Unit COGS",
    cell: formatCurrencyCell,
    size: 125,
    minSize: 125,
    maxSize: 200,
  }),
  col.accessor("wholesaleUnitPriceRange", {
    header: "Wholesale Unit Price",
    cell: formatCurrencyRangeCell,
    size: 150,
    minSize: 150,
    maxSize: 200,
  }),
  col.accessor("unitSrp", {
    header: "Unit SRP",
    cell: formatCurrencyCell,
    size: 125,
    minSize: 125,
    maxSize: 200,
  }),
  col.accessor(
    (r) => {
      if (getIsProductLine(r)) return null;
      return r.productLine?.innerCaseQuantity ?? null;
    },
    {
      id: "innerCaseQuantity",
      header: "Inner Case Quantity",
      cell: formatIntegerCell,
      size: 115,
    },
  ),
  col.accessor(
    (r) => {
      if (getIsProductLine(r)) return null;
      return r.productLine?.wholesaleCaseQuantity ?? null;
    },
    {
      id: "wholesaleCaseQuantity",
      header: "Case Quantity",
      cell: formatIntegerCell,
      size: 110,
    },
  ),
  col.display({
    id: "dimensions",
    header: "Dimensions",
    cell: (cell) => {
      const sku = cell.row.original;
      if (getIsProductLine(sku) || !sku.skuComponentsList) return null;
      return (
        <Flex gap={1} flexDirection="column" alignItems="flex-start">
          {sortSkuComponents(sku.skuComponentsList).map((skuComponent) => {
            if (!skuComponent.dimensions?.id) return null;
            return (
              <LabeledValueBadge
                key={skuComponent.id}
                label={
                  SKU_COMPONENT_TYPE_LABELS_SHORT[skuComponent.skuComponentType]
                }
                value={skuComponent.dimensions.formatted}
                labelProps={{ minW: "4.1em", textAlign: "center" }}
              />
            );
          })}
        </Flex>
      );
    },
    size: 200,
  }),
  col.display({
    id: "caseCube",
    header: "Case Cube",
    cell: (cell) => {
      if (getIsProductLine(cell.row.original)) return null;
      const caseSkuComponent = cell.row.original.skuComponentsList.find(
        (sc) => sc.skuComponentType === SkuComponentType.CASE,
      );
      return formatCaseCube(caseSkuComponent?.dimensions?.cube);
    },
  }),
  col.display({
    id: "grossCaseWeightLbs",
    header: "Gross Case Weight",
    cell: (cell) => {
      if (getIsProductLine(cell.row.original)) return null;
      const caseSkuComponent = cell.row.original.skuComponentsList.find(
        (sc) => sc.skuComponentType === SkuComponentType.CASE,
      );
      return formatWeight(caseSkuComponent?.grossWeightLbs);
    },
  }),
  col.accessor("grossPalletWeightLbs", {
    header: "Gross Pallet Weight",
    cell: (cell) => {
      if (getIsProductLine(cell.row.original)) return null;
      return formatWeight(cell.row.original.grossPalletWeightLbs);
    },
  }),
];

const columnPinning: ColumnPinningState = { left: ["name"] };

type Props = {
  data: ProductsTableData | null | undefined;
  isCreateDisabled?: boolean;
  isLoading?: boolean;
  onCreateNew: (createNew: CreateNew) => void;
  onRowSelected: (typename: RowTypes, id: string) => void;
  placeholderText?: string;
  selectedRow: [typename: RowTypes, id: string] | null | undefined;
};

export default function ProductsTable({
  data: rawData,
  isCreateDisabled,
  isLoading,
  onCreateNew,
  onRowSelected,
  placeholderText = "No product lines have been added to your account.",
  selectedRow,
}: Props) {
  const [softBlue] = useToken("colors", ["softBlue"]);

  const productLines = rawData?.productLinesList;

  const initialState = useMemo(
    () => ({
      expanded: productLines?.map((pl, i) => ({ [i]: true })) || {},
      columnPinning,
    }),
    [productLines],
  );

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(
      getFragmentData(
        productLinesSkusTableFieldsFragment,
        productLines,
      ) as readonly ProductLineDatum[],
    ),
    initialState,
    state: initialState,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (originalRow) =>
      getIsProductLine(originalRow)
        ? makeNotReadonly(
            getFragmentData(
              tableSkuFragment,
              getFragmentData(tableSkusForProductLineFragment, originalRow)
                .skusList,
            ),
          )
        : [],
  });

  const tableProps = useChakraReactTableProps([
    useFixedLayout(tableInstance),
    useStickyHeader<Datum>(),
    useSelectableRow<Datum>({
      getIdForRow: (r) => `${r.original.__typename}:${r.original.id}`,
      selected: selectedRow && `${selectedRow[0]}:${selectedRow[1]}`,
      onSelectedChange: (combinedId) => {
        const [typename, id] = combinedId.split(":");
        onRowSelected(typename as RowTypes, id);
      },
    }),
    {
      getCellProps: (cell) => {
        if (!getIsProductLine(cell.row.original)) return {};
        return {
          py: 0,
          pb: "5px",
          whiteSpace: "nowrap",
          top: HEADER_HEIGHT,
          zIndex: 2,
          backgroundColor: "inherit",
          position: "sticky",
          boxShadow: `inset 0px -5px 0px ${softBlue} !important`,
          sx: {
            "&[data-pinning]": { zIndex: 3 },
          },
        };
      },
      getColumnProps: (column) => {
        if (["name", "upcs"].includes(column.id)) return {};
        return { textAlign: "center" };
      },
      getRowProps: (row) => ({
        backgroundColor: "white",
        "data-typename": row.original.__typename,
      }),
      getHeaderProps: () => ({
        verticalAlign: "bottom",
        height: HEADER_HEIGHT,
      }),
      tableProps: { marginX: { base: 0, md: 12 } },
      tableBodyProps: {
        sx: {
          "tr[data-typename=ProductLine]": {
            borderBottomWidth: 0,
          },
          "tr[data-typename=Sku]": {
            borderBottomWidth: 0,
          },
          "tr[data-typename=Sku] + tr[data-typename=Sku]": {
            borderTopWidth: 1,
          },
        },
      },
      noRowsPlaceholderProps: {
        textAlign: "center",
        background: "cardBackground",
        color: "gray.500",
        py: 10,
        px: 5,
        borderBottomLeftRadius: "md",
        borderBottomRightRadius: "md",
      },
    },
  ]);

  return (
    <ChakraReactTable
      tableInstance={tableInstance}
      {...tableProps}
      isLoading={isLoading}
      noRowsPlaceholder={
        <>
          <Text>{placeholderText}</Text>
          {!isCreateDisabled && (
            <Text mt={4}>
              <Button
                colorScheme="pinkButton"
                size="sm"
                onClick={() => onCreateNew(["ProductLine"])}
                autoFocus
              >
                <Text as="span" mr=".5em" fontSize="1.4em">
                  +
                </Text>{" "}
                Add Product Line
              </Button>
            </Text>
          )}
        </>
      }
    />
  );
}
